import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import { getFileTypeInfo, formatFileSize, truncateFileName } from '../utils/fileUtils';

const BoardEditPage: React.FC = () => {
  const { category, id } = useParams<{ category: string; id: string }>();
  const navigate = useNavigate();
  const [board, setBoard] = useState<any>(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [links, setLinks] = useState('');
  const [isPinned, setIsPinned] = useState(false);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<any[]>([]);
  const [newFile, setNewFile] = useState<File | null>(null);

  useEffect(() => {
    if (id) {
      // 게시글 정보 불러오기
      axiosInstance.get(`/api/boards/${id}`)
        .then(res => {
          const boardData = res.data;
          setBoard(boardData);
          setTitle(boardData.title);
          setContent(boardData.content);
          setLinks(boardData.lnks || '');
          setIsPinned(boardData.is_pinned);
        })
        .catch(err => {
          alert('게시글을 불러올 수 없습니다.');
          navigate(`/boards/${category}`);
        });
      
      // 첨부파일 목록 불러오기
      axiosInstance.get(`/api/boards/attach/${id}`)
        .then(res => setFiles(res.data))
        .catch(err => console.error('첨부파일 로딩 실패:', err));
    }
  }, [id, category, navigate]);

  // 첨부파일 삭제
  const handleDeleteFile = async (fileId: number) => {
    if (!window.confirm('첨부파일을 삭제하시겠습니까?')) return;
    
    try {
      await axiosInstance.delete(`/api/boards/attach/${fileId}`);
      setFiles(files.filter(file => file.id !== fileId));
      alert('첨부파일이 삭제되었습니다.');
    } catch (err: any) {
      alert('첨부파일 삭제에 실패했습니다.');
    }
  };

  // 새 첨부파일 추가
  const handleAddFile = async () => {
    if (!newFile) return;
    
    try {
      const formData = new FormData();
      formData.append('file', newFile);
      formData.append('board_id', id!);
      formData.append('description', '');
      
      const res = await axiosInstance.post('/api/boards/attach', formData, { 
        headers: { 'Content-Type': 'multipart/form-data' } 
      });
      
      // 새로운 파일 목록 가져오기
      const filesRes = await axiosInstance.get(`/api/boards/attach/${id}`);
      setFiles(filesRes.data);
      setNewFile(null);
      
      // 파일 input 초기화
      const fileInput = document.getElementById('newFileInput') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
      alert('첨부파일이 추가되었습니다.');
    } catch (err: any) {
      alert('첨부파일 추가에 실패했습니다.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) {
      alert('제목과 내용을 입력해주세요.');
      return;
    }

    setLoading(true);
    try {
      await axiosInstance.put(`/api/boards/${id}`, {
        title: title.trim(),
        content: content.trim(),
        lnks: links.trim() || null,
        is_pinned: isPinned
      });

      alert('게시글이 수정되었습니다.');
      navigate(`/boards/${category}/${id}`);
    } catch (err: any) {
      if (err.response?.status === 401) {
        alert('로그인이 필요합니다.');
        navigate('/login');
      } else if (err.response?.status === 403) {
        alert('수정 권한이 없습니다.');
        navigate(`/boards/${category}/${id}`);
      } else {
        alert('게시글 수정에 실패했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('수정을 취소하시겠습니까?')) {
      navigate(`/boards/${category}/${id}`);
    }
  };

  if (!board) return <div>Loading...</div>;

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 24 }}>
      <h2>게시글 수정</h2>
      
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
            제목 *
          </label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            style={{
              width: '100%',
              padding: 12,
              border: '1px solid #ddd',
              borderRadius: 4,
              fontSize: 16
            }}
            required
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
            내용 *
          </label>
          <textarea
            value={content}
            onChange={e => setContent(e.target.value)}
            style={{
              width: '100%',
              minHeight: 300,
              padding: 12,
              border: '1px solid #ddd',
              borderRadius: 4,
              fontSize: 14,
              resize: 'vertical'
            }}
            required
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
            관련링크 (여러 개인 경우 ;로 구분)
          </label>
          <input
            type="text"
            value={links}
            onChange={e => setLinks(e.target.value)}
            placeholder="https://example.com;https://example2.com"
            style={{
              width: '100%',
              padding: 12,
              border: '1px solid #ddd',
              borderRadius: 4,
              fontSize: 14
            }}
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={isPinned}
              onChange={e => setIsPinned(e.target.checked)}
              style={{ marginRight: 8 }}
            />
            고정하기
          </label>
        </div>

        {/* 기존 첨부파일 섹션 */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
            기존 첨부파일
          </label>
          {files.length === 0 ? (
            <div style={{ padding: 16, backgroundColor: '#f8f9fa', borderRadius: 4, color: '#6c757d' }}>
              첨부파일이 없습니다.
            </div>
          ) : (
            <div style={{ border: '1px solid #ddd', borderRadius: 4, padding: 12 }}>
              {files.map(file => {
                // file_type이 이미 라벨형태인지 MIME 타입인지 확인
                const isLabel = !file.file_type?.includes('/') && !file.file_type?.includes('application');
                const fileInfo = isLabel 
                  ? (() => {
                      // 라벨에서 적절한 아이콘 선택
                      const labelIconMap: Record<string, string> = {
                        'Word 문서': '📄',
                        'Excel 문서': '📊', 
                        'PowerPoint 문서': '📽️',
                        'PDF 문서': '📋',
                        '이미지': '🖼️',
                        '비디오': '🎬',
                        '오디오': '🎵',
                        '압축 파일': '📦',
                        '텍스트': '📝'
                      };
                      const icon = labelIconMap[file.file_type] || '📎';
                      const colorMap: Record<string, string> = {
                        'Word 문서': '#2b579a',
                        'Excel 문서': '#217346',
                        'PowerPoint 문서': '#d24726',
                        'PDF 문서': '#dc3545',
                        '이미지': '#6f42c1',
                        '비디오': '#fd7e14',
                        '오디오': '#20c997',
                        '압축 파일': '#6c757d',
                        '텍스트': '#495057'
                      };
                      const color = colorMap[file.file_type] || '#007bff';
                      return { icon, label: file.file_type, color };
                    })()
                  : getFileTypeInfo(file.file_name, file.file_type);

                return (
                  <div key={file.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRadius: 4,
                    marginBottom: 8
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <span style={{ fontSize: '20px', marginRight: 8 }}>
                        {fileInfo.icon}
                      </span>
                      <div>
                        <div style={{ fontWeight: '500', color: fileInfo.color }}>
                          {truncateFileName(file.file_name, 30)}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6c757d' }}>
                          {fileInfo.label} {file.file_size && `• ${formatFileSize(file.file_size)}`}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteFile(file.id)}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      삭제
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 새 첨부파일 추가 섹션 */}
        <div style={{ marginBottom: 30 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
            새 첨부파일 추가
          </label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              id="newFileInput"
              type="file"
              onChange={e => setNewFile(e.target.files?.[0] || null)}
              style={{ flex: 1 }}
            />
            <button
              type="button"
              onClick={handleAddFile}
              disabled={!newFile}
              style={{
                padding: '8px 16px',
                backgroundColor: newFile ? '#28a745' : '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: newFile ? 'pointer' : 'not-allowed',
                fontSize: '14px'
              }}
            >
              추가
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={handleCancel}
            style={{
              padding: '12px 24px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer'
            }}
          >
            취소
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '12px 24px',
              backgroundColor: loading ? '#6c757d' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? '수정 중...' : '수정하기'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BoardEditPage; 